import { unsafeDecodeJwtPayload } from './jwt-unsafe';
import env from './env';

// DEV-715 — how the user authenticated, read from the id_token `sub`.
//
// Auth0 prefixes the sub with the connection strategy. FI/SE passwordless
// logins are `sms|…` and `email|…`; everything else came through a
// federated or database connection that peikko redirected to itself:
// `waad|` (Microsoft staff), `auth0|` (UK roster), `oauth2|<skolon
// connection>|` (Skolon), and later `google-apps|` (DEV-717).
//
// Redirect hint only (see utils/jwt-unsafe.ts): the token is decoded, not
// verified. Worst case a forged sub routes someone to /voucher, which still
// needs a jakari-verified token to do anything.
const PASSWORDLESS_SUB_PREFIXES = ['sms|', 'email|'];

export const isFederatedSub = (sub: unknown): boolean =>
  typeof sub === 'string' &&
  sub.length > 0 &&
  !PASSWORDLESS_SUB_PREFIXES.some((prefix) => sub.startsWith(prefix));

// A malformed or missing token is NOT federated, so it keeps the existing
// passwordless behaviour.
export const isFederatedToken = (token?: string | null): boolean =>
  isFederatedSub(unsafeDecodeJwtPayload(token ?? '')?.sub);

// Skolon tile users. They never have a voucher code to type, so getRedirect
// sends them back to /login-skolon instead of /voucher (DEV-677).
//
// Auth0 builds a generic-OAuth2 sub as `oauth2|<connection name>|<id>`, and
// connection names are per-tenant and case-sensitive — this repo already has
// `microsoft` on dev/test vs `Microsoft` on prod. So the prefix is derived
// from NEXT_PUBLIC_SKOLON_CONNECTION_NAME rather than hardcoded, and the
// comparison is case-insensitive in case the env var and Auth0 disagree on
// casing. Hardcoding `oauth2|skolon|` would have made every Skolon branch in
// this file silently revert to /voucher in prod alone, if the connection was
// created there as `Skolon`.
const SKOLON_CONNECTION_FALLBACK = 'skolon';

const skolonSubPrefix = (): string => {
  const connection =
    (env?.NEXT_PUBLIC_SKOLON_CONNECTION_NAME || '').trim() ||
    SKOLON_CONNECTION_FALLBACK;
  return `oauth2|${connection.toLowerCase()}|`;
};

export const isSkolonSub = (sub: unknown): boolean =>
  typeof sub === 'string' && sub.toLowerCase().startsWith(skolonSubPrefix());

export const isSkolonToken = (token?: string | null): boolean =>
  isSkolonSub(unsafeDecodeJwtPayload(token ?? '')?.sub);

// DEV-794 — a pupil who signed in through their school's own generic-OAuth2
// connection (e.g. `crosthwaite-google`). Auth0 forms the sub as
// `oauth2|<connection>|<id>`. Like Skolon users they hold no voucher code,
// so getRedirect sends them to /login-school?from=app instead of /voucher.
//
// Staff Google uses the generic `google-oauth2` social connection, whose sub
// is `google-oauth2|<id>` — no `oauth2|` prefix — so a teacher is never
// mistaken for a pupil here.
const OAUTH2_SUB = /^oauth2\|([^|]+)\|/;

// The connection name as it appears in the sub, case preserved (connection
// names are case-sensitive per tenant). null when the sub is not `oauth2|`.
export const getOauth2Connection = (sub: unknown): string | null => {
  if (typeof sub !== 'string') return null;
  const match = OAUTH2_SUB.exec(sub);
  return match ? match[1] : null;
};

// Which `oauth2|` connections ARE school SSO: an explicit allow-list from
// NEXT_PUBLIC_SCHOOL_SSO_CONNECTIONS (comma-separated, set per env in
// kontemplate like the other connection names), lowercased.
//
// An allow-list, not "every oauth2| connection except Skolon": a future
// generic-OAuth2 connection for anything else (another tile provider, a
// partner SSO) would otherwise be routed to /login-school and activated
// against a pupil voucher by default. Unknown connections now fall back to
// the ordinary federated handling (/voucher), which is the safe failure.
//
// Why env and not the school's door config, which already carries the
// connection name: getRedirect has only cookies to go on — the school's
// doors would cost an identify lookup on every request, and a pupil whose
// school cookie is gone (new browser profile, cleared cookies) would stop
// being recognised. The sub alone has to be enough, as it is for Skolon.
//
// ⚠️ Every pupil connection a school's google door names must be listed
// here too, or its pupils land on /voucher after signing in.
export const getSchoolSsoConnections = (): string[] =>
  (env?.NEXT_PUBLIC_SCHOOL_SSO_CONNECTIONS || '')
    .split(',')
    .map((name: string) => name.trim().toLowerCase())
    .filter(Boolean);

// Skolon is excluded even if someone lists it: it has its own route and
// screens, and jakari's /user/sso/activate rejects it — so peikko never
// sends a Skolon pupil down a route that would 403 them.
//
// `pupilDoor` widens the allow-list for ONE device (review 2026-10-23, S5):
// where the school cookie says this device's pupil door is `google`, any
// non-Skolon `oauth2|` sub is a school-SSO pupil too. That device was set up
// by /school for a school-Google school, so an `oauth2|` sign-in on it came
// through that door — and a connection someone forgot to add to
// NEXT_PUBLIC_SCHOOL_SSO_CONNECTIONS should not strand its pupils on
// /voucher. /login-school logs a Sentry warning when it activates one, so
// the env gap gets fixed. Without the cookie the allow-list alone decides,
// as before.
export const isSchoolSsoSub = (
  sub: unknown,
  { pupilDoor }: { pupilDoor?: string } = {},
): boolean => {
  const connection = getOauth2Connection(sub)?.toLowerCase();
  if (
    !connection ||
    connection === SKOLON_CONNECTION_FALLBACK ||
    isSkolonSub(sub)
  ) {
    return false;
  }
  return (
    pupilDoor === 'google' || getSchoolSsoConnections().includes(connection)
  );
};

export const isSchoolSsoToken = (
  token?: string | null,
  options: { pupilDoor?: string } = {},
): boolean => isSchoolSsoSub(unsafeDecodeJwtPayload(token ?? '')?.sub, options);

// Whether this connection is on the allow-list — /login-school's check for
// the S5 warning above.
export const isListedSchoolSsoConnection = (
  connection: string | null,
): boolean =>
  Boolean(connection) &&
  getSchoolSsoConnections().includes(connection.toLowerCase());
